import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Svg from 'components/atoms/Svg/Svg'
import Modal from 'components/molecules/Modal/Modal'
import Button from 'components/atoms/Button/Button'
import Timer from 'components/molecules/Timer/Timer'
import styles from './OtpVerification.module.scss'

type Props = {
  isOpen: boolean
  /** Where the code was sent — an email address or cell number, shown to the user */
  sentTo?: string
  failed?: boolean
  onSubmit?: (pin: { pin: string }) => void
  onTimeOut?: () => void
  resend?: () => void
  onClose: () => void
}

const PIN_LENGTH = 6
const RESEND_SECONDS = 5 * 60

const OtpVerification: React.FC<Props> = ({ isOpen, sentTo, failed, onSubmit, onTimeOut, resend, onClose }) => {
  const [inputs, setInputs] = useState<string[]>(() => Array(PIN_LENGTH).fill(''))
  const [hasTimedout, setHasTimedout] = useState(false)
  const [timerKey, setTimerKey] = useState(0)
  const pin = inputs.join('')

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (!isOpen) return
    const id = setTimeout(() => {
      inputRefs.current[0]?.focus()
    }, 200)
    return () => clearTimeout(id)
  }, [isOpen])

  useEffect(() => {
    if (failed) {
      setInputs(Array(PIN_LENGTH).fill(''))
      inputRefs.current[0]?.focus()
    }
  }, [failed])

  const isValid = inputs.every(Boolean)

  const fillFrom = (index: number, digits: string): void => {
    const chars = digits.slice(0, PIN_LENGTH - index).split('')
    setInputs(prev => {
      const next = [...prev]
      chars.forEach((char, i) => {
        next[index + i] = char
      })
      return next
    })
    inputRefs.current[Math.min(index + chars.length, PIN_LENGTH - 1)]?.focus()
  }

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.replace(/\D/g, '')

    if (value === '') {
      setInputs(prev => prev.map((v, i) => (i === index ? '' : v)))
      return
    }

    if (value.length > 1) {
      fillFrom(index, value)
      return
    }

    setInputs(prev => prev.map((v, i) => (i === index ? value : v)))
    inputRefs.current[index + 1]?.focus()
  }

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const digits = e.clipboardData.getData('text').replace(/\D/g, '')
    if (!digits) return
    fillFrom(digits.length >= PIN_LENGTH ? 0 : index, digits)
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !inputs[index]) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowLeft') {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight') {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleResend = (): void => {
    setHasTimedout(false)
    setInputs(Array(PIN_LENGTH).fill(''))
    setTimerKey(key => key + 1)
    if (typeof resend === 'function') {
      resend()
    }
    inputRefs.current[0]?.focus()
  }

  const handleTimerComplete = (): void => {
    setHasTimedout(true)
    onTimeOut?.()
  }

  const handleSubmit = (): void => {
    if (typeof onSubmit === 'function') {
      onSubmit({ pin })
    }
  }

  return (
    <Modal isOpen={isOpen} padding="md" theme="light" onClose={onClose}>
      <div className={styles.root}>
        <Svg name="otp-shield-and-ring" width="64px" height="64px" />

        <h2 className={styles.title}>Enter verification code</h2>

        <p className={styles.instruction}>
          {sentTo ? (
            <>
              We sent a {PIN_LENGTH}-digit code to <strong>{sentTo}</strong>.
            </>
          ) : (
            <>
              Please request a QR code from the dev team. You need a 2FA app that supports 60 second OTPs. Download{' '}
              <a href="https://getaegis.app/" target="_blank" rel="noreferrer">
                Aegis
              </a>{' '}
              or{' '}
              <a href="https://apps.apple.com/us/app/authenticator-app/id1538761576" target="_blank" rel="noreferrer">
                Authenticator App
              </a>
              .
            </>
          )}
        </p>

        <div className={classNames(styles.pin, { [styles.pinError]: failed })}>
          {inputs.map((value, index) => (
            <input
              key={index}
              disabled={hasTimedout}
              name={`input${index + 1}`}
              value={value}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${index + 1}`}
              className={classNames({ [styles.filled]: value })}
              ref={el => {
                inputRefs.current[index] = el
              }}
              onChange={e => handleChange(index, e)}
              onPaste={e => handlePaste(index, e)}
              onKeyDown={e => handleKeyDown(index, e)}
            />
          ))}
        </div>

        {failed && <p className={styles.error}>That code didn&apos;t match. Try again.</p>}

        {sentTo && (
          <p className={styles.resend}>
            {hasTimedout ? (
              <button type="button" className={styles.resendLink} onClick={handleResend}>
                Resend code
              </button>
            ) : (
              <>
                Resend code in{' '}
                <span className={styles.countdown}>
                  <Timer key={timerKey} duration={RESEND_SECONDS} onComplete={handleTimerComplete} />
                </span>
              </>
            )}
          </p>
        )}

        <div className={styles.actions}>
          <Button block disabled={!isValid || hasTimedout} onClick={handleSubmit}>
            Verify
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default OtpVerification
